import { http } from "./http";

export type PromotionStatus = "draft" | "active" | "paused" | "archived";
export type PromotionChannel = "online" | "pos" | "both";

export type PromotionTriggerType =
  | "buy_x"
  | "spend_threshold"
  | "cart_contains_product"
  | "cart_contains_category";

export type PromotionRewardType =
  | "free_product"
  | "item_discount"
  | "cart_discount";

export type PromotionDiscountType = "fixed" | "percentage";
export type PromotionTargetRole = "trigger" | "reward" | "eligible";

export type AdminPromotionTrigger = {
  id?: number;
  promotion_id?: number;
  trigger_type: PromotionTriggerType;
  required_quantity?: number | null;
  required_subtotal?: number | string | null;
  config?: Record<string, unknown>;
};

export type AdminPromotionReward = {
  id?: number;
  promotion_id?: number;
  reward_type: PromotionRewardType;
  discount_type?: PromotionDiscountType | null;
  discount_value?: number | string | null;
  max_discount?: number | string | null;
  reward_quantity?: number | null;
  config?: Record<string, unknown>;
};

export type AdminPromotionProduct = {
  id?: number;
  promotion_id?: number;
  role: PromotionTargetRole;
  product_id: number;
};

export type AdminPromotionCategory = {
  id?: number;
  promotion_id?: number;
  role: PromotionTargetRole;
  category_id: number;
};

export type AdminPromotion = {
  id: number;
  name: string;
  slug: string;
  status: PromotionStatus;
  channel: PromotionChannel;
  priority: number;
  is_stackable_with_coupons: boolean;
  is_stackable_with_promotions: boolean;
  usage_limit: number | null;
  usage_per_customer_limit: number | null;
  starts_at: string | null;
  ends_at: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  total_used?: number;

  triggers?: AdminPromotionTrigger[];
  rewards?: AdminPromotionReward[];
  products?: AdminPromotionProduct[];
  categories?: AdminPromotionCategory[];
};

export type AdminPromotionPayload = {
  name: string;
  slug?: string;
  status: PromotionStatus;
  channel: PromotionChannel;
  priority: number;
  is_stackable_with_coupons: boolean;
  is_stackable_with_promotions: boolean;
  usage_limit?: number | null;
  usage_per_customer_limit?: number | null;
  starts_at?: string | null;
  ends_at?: string | null;
  admin_notes?: string | null;
  triggers: AdminPromotionTrigger[];
  rewards: AdminPromotionReward[];
  products: AdminPromotionProduct[];
  categories: AdminPromotionCategory[];
};

export async function listAdminPromotions() {
  const res = await http.get<AdminPromotion[]>("/api/admin/promotions");
  return res.data;
}

export async function getAdminPromotion(id: number) {
  const res = await http.get<AdminPromotion>(`/api/admin/promotions/${id}`);
  return res.data;
}

export async function createAdminPromotion(payload: AdminPromotionPayload) {
  const res = await http.post<AdminPromotion>("/api/admin/promotions", payload);
  return res.data;
}

export async function updateAdminPromotion(id: number, payload: AdminPromotionPayload) {
  const res = await http.put<AdminPromotion>(`/api/admin/promotions/${id}`, payload);
  return res.data;
}

export async function updateAdminPromotionStatus(id: number, status: PromotionStatus) {
  const res = await http.patch<AdminPromotion>(`/api/admin/promotions/${id}/status`, {
    status,
  });
  return res.data;
}

export async function deleteAdminPromotion(id: number) {
  const res = await http.delete<{ ok: boolean; id: number }>(
    `/api/admin/promotions/${id}`
  );
  return res.data;
}